import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Crop } from "lucide-react";
import { api, assetUrl, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { PhotoEditor } from "./PhotoEditor";

// Bake the camera's EXIF orientation into the pixels so the editor and saved photo are upright.
const uprightImage = async (file) => {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const c = document.createElement("canvas");
    c.width = bmp.width; c.height = bmp.height;
    c.getContext("2d").drawImage(bmp, 0, 0);
    bmp.close?.();
    return await new Promise((resolve) => c.toBlob((b) => resolve(b || file), "image/jpeg", 0.95));
  } catch {
    return file;
  }
};

const btn = "btn-press inline-flex min-h-[46px] items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:opacity-60";

export const PhotoUploader = ({ employee, onChange }) => {
  const { t } = useLang();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState("");

  const run = async (request) => {
    setBusy(true);
    try {
      const { data } = await request();
      onChange(data);
      toast.success(t("saved"));
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const close = () => { if (src.startsWith("blob:")) URL.revokeObjectURL(src); setSrc(""); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) setSrc(URL.createObjectURL(await uprightImage(file)));
  };
  const editCurrent = () => setSrc(assetUrl(`/api/public/cards/${employee.slug}/photo?v=${encodeURIComponent(employee.updated_at || "")}`));
  const upload = async (blob) => {
    const form = new FormData();
    form.append("file", blob, "photo.jpg");
    if (await run(() => api.post("/me/photo", form))) close();
  };
  const remove = () => {
    if (window.confirm(t("Remove your photo?"))) run(() => api.delete("/me/photo"));
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-3xl border border-slate-200/80 bg-white p-5" data-testid="photo-uploader">
      <p className="me-auto text-xs font-bold uppercase tracking-wider text-slate-500">{t("photo")}</p>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="photo-file-input" />
      <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid="photo-upload-button" className={`${btn} bg-slate-900 text-white`}>
        <Camera className="h-4 w-4" /> {employee.has_photo ? t("Change photo") : t("upload_photo")}
      </button>
      {employee.has_photo && (
        <>
          <button type="button" disabled={busy} onClick={editCurrent} data-testid="photo-edit-button"
            className={`${btn} border border-slate-300 text-slate-700 hover:border-slate-900`}>
            <Crop className="h-4 w-4" /> {t("Crop / rotate")}
          </button>
          <button type="button" disabled={busy} onClick={remove} data-testid="photo-remove-button"
            className={`${btn} border border-slate-300 text-slate-700 hover:border-red-400 hover:text-red-600`}>
            <Trash2 className="h-4 w-4" /> {t("remove_photo")}
          </button>
        </>
      )}
      <PhotoEditor src={src} busy={busy} onCancel={close} onSave={upload} />
    </div>
  );
};
